import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";
import { motion, AnimatePresence } from "framer-motion";

function getSessionId() {
  let id = localStorage.getItem("pn_chat_session");
  if (!id) {
    id = "sess-" + Math.random().toString(36).slice(2) + Date.now();
    localStorage.setItem("pn_chat_session", id);
  }
  return id;
}

const QUICK_QUESTIONS = [
  "Combien coûte un site web ?",
  "Comment apparaître sur Google ?",
  "Comment automatiser mon entreprise ?",
  "Quels services proposez-vous ?",
];

export default function ChatbotWidget() {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content:
        "Bonjour ! Je suis Passerelle AI 👋 Comment puis-je vous aider à développer votre entreprise grâce au numérique ?",
    },
  ]);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || loading) return;
    setMessages((m) => [...m, { role: "user", content }]);
    setInput("");
    setLoading(true);
    try {
      const { data } = await api.post("/chat", {
        session_id: getSessionId(),
        message: content,
      });
      setMessages((m) => [...m, { role: "assistant", content: data.response }]);
    } catch {
      setMessages((m) => [...m, { role: "assistant", content: "Désolé, une erreur est survenue. Réessayez ou prenez un RDV via le bouton Diagnostic gratuit." }]);
    } finally { setLoading(false); }
  };

  return (
    <>
      <button
        onClick={() => setOpen(!open)}
        data-testid="chatbot-toggle"
        className="fixed bottom-6 right-6 z-50 bg-[#00B8D9] hover:bg-[#0096B3] text-white rounded-full p-4 shadow-xl shadow-[#00B8D9]/30 transition-all hover:scale-110"
        aria-label="Chat"
      >
        {open ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6" />}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            data-testid="chatbot-window"
            className="fixed bottom-24 right-6 z-50 w-[calc(100vw-3rem)] sm:w-[400px] h-[560px] bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden"
          >
            <div className="bg-[#0B1F3A] text-white p-4 flex items-center gap-3">
              <div className="p-2 rounded-full bg-[#00B8D9]"><Sparkles className="h-4 w-4" /></div>
              <div>
                <div className="font-heading font-bold">Passerelle AI</div>
                <div className="text-xs text-cyan-200 flex items-center gap-1">
                  <span className="h-1.5 w-1.5 rounded-full bg-green-400 inline-block"></span>
                  En ligne · Réponse immédiate
                </div>
              </div>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#F5F7FA]" data-testid="chatbot-messages">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm ${
                    m.role === "user"
                      ? "bg-[#00B8D9] text-white rounded-br-sm"
                      : "bg-white text-[#0B1F3A] border border-gray-200 rounded-bl-sm"
                  }`}>
                    {m.content}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="bg-white border border-gray-200 rounded-2xl rounded-bl-sm px-4 py-2.5">
                    <div className="flex gap-1">
                      <span className="h-2 w-2 bg-[#00B8D9] rounded-full animate-bounce"></span>
                      <span className="h-2 w-2 bg-[#00B8D9] rounded-full animate-bounce" style={{ animationDelay: "0.1s" }}></span>
                      <span className="h-2 w-2 bg-[#00B8D9] rounded-full animate-bounce" style={{ animationDelay: "0.2s" }}></span>
                    </div>
                  </div>
                </div>
              )}
              {messages.length === 1 && !loading && (
                <div className="space-y-2 pt-2">
                  <div className="text-xs text-[#4B5563] font-semibold">Questions fréquentes :</div>
                  {QUICK_QUESTIONS.map((q) => (
                    <button
                      key={q}
                      onClick={() => send(q)}
                      data-testid={`chatbot-quick-${q.slice(0, 10)}`}
                      className="block w-full text-left text-sm px-3 py-2 bg-white border border-gray-200 rounded-lg hover:border-[#00B8D9] hover:bg-[#00B8D9]/5 transition-colors text-[#0B1F3A]"
                    >{q}</button>
                  ))}
                </div>
              )}
            </div>

            <form
              onSubmit={(e) => { e.preventDefault(); send(); }}
              className="p-3 border-t border-gray-200 bg-white flex gap-2"
            >
              <Input
                data-testid="chatbot-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Posez votre question..."
                disabled={loading}
                className="flex-1"
              />
              <Button type="submit" disabled={loading || !input.trim()} className="bg-[#00B8D9] hover:bg-[#0096B3]" data-testid="chatbot-send">
                <Send className="h-4 w-4" />
              </Button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
